import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { useLinkStatus } from '@/ui/useLinkStatus';
import { linkController } from '@/giraffe/linkController';
import { getCell } from '@/model/workbook';
import type { SaveStatus } from '@/giraffe/persist';

const LINK_LABEL = {
  unlinked: 'Unlinked',
  'linked-unsaved': 'Linked, unsaved',
  'linked-saved': 'Linked, saved',
} as const;

const SAVE_LABEL = {
  idle: '',
  pending: 'Unsaved',
  saving: 'Saving…',
  saved: 'Saved',
  retrying: 'Retrying save',
  error: 'Save failed',
} as const;

const BRIDGE_LABEL = {
  offline: 'No project',
  connecting: 'Connecting…',
  connected: 'Live',
} as const;

const BRIDGE_TITLE = {
  offline: 'Running standalone — GI.* functions have no data to read.',
  connecting: 'Waiting for the Giraffe bridge. The first reads race the handshake and come back empty.',
  connected: 'Reading live project data.',
} as const;

/**
 * Selection stats plus the standing warning count. A summed rate is allowed but
 * it must be *visible* — a chip here is what stops the warning being something
 * you only find by hovering the one cell you already doubted.
 */
export function StatusBar({
  bridge,
  save,
}: {
  bridge: 'offline' | 'connecting' | 'connected';
  save: SaveStatus | null;
}): JSX.Element {
  useStore();
  const link = useLinkStatus();
  const sheet = store.sheet();
  const { r1, c1, r2, c2 } = store.sel;

  let count = 0;
  let numeric = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      if (!getCell(sheet, r, c)) continue;
      count += 1;
      const v = store.engine.valueAt(sheet, r, c);
      if (typeof v === 'number' && Number.isFinite(v)) {
        numeric += 1;
        sum += v;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }

  const rateWarnings = store.warnings();
  const stats = store.engine.stats();
  const fmt = (n: number): string => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="gt-status">
      {numeric > 0 ? (
        <>
          <span>Sum {fmt(sum)}</span>
          <span>Avg {fmt(sum / numeric)}</span>
          <span>Min {fmt(min)}</span>
          <span>Max {fmt(max)}</span>
        </>
      ) : null}
      <span>{count} filled</span>
      <div className="gt-tb-spacer" />
      {rateWarnings.length > 0 ? (
        <>
          <span className="gt-chip is-warn" title={rateWarnings.map((d) => d.message).join('\n')}>
            ⚠ {rateWarnings.length} measure {rateWarnings.length === 1 ? 'warning' : 'warnings'}
          </span>
          {/* The marks on the cells are a separate ask: the count says
              something is worth a look, this says show me where. */}
          <button
            className={`gt-chip-toggle${store.showWarnings ? ' is-on' : ''}`}
            onClick={() => store.toggleWarnings()}
            title={
              store.showWarnings
                ? 'Stop marking the cells these warnings are about'
                : 'Mark the cells these warnings are about, in amber'
            }
          >
            {store.showWarnings ? 'Hide cells' : 'Show cells'}
          </button>
        </>
      ) : null}
      {stats.circular > 0 ? (
        <span className="gt-chip is-error">Circular references: {stats.circular}</span>
      ) : null}
      <span className="gt-dim">
        {stats.formulas} formulas · {stats.volatile} live
      </span>
      {save ? (
        <span className={`gt-save is-${save.state}`} title={save.detail}>
          {SAVE_LABEL[save.state]}
        </span>
      ) : null}
      <span className={`gt-bridge is-${bridge}`} title={BRIDGE_TITLE[bridge]}>
        ● {BRIDGE_LABEL[bridge]}
      </span>
      {store.devMode ? (
        <button className={`gt-link is-${link.kind}`} onClick={() => linkController.open()} title={link.detail}>
          ● {LINK_LABEL[link.kind]}
          {link.fileName ? ` — ${link.fileName}` : ''}
        </button>
      ) : null}
    </div>
  );
}
